import { useCallback, useEffect, useState } from 'react';
import { aws } from '../lib/awsdb';
import { useToast } from '../lib/Toast';
import Dashboard from './Dashboard';
import Receivables from './Receivables';
import Customers from './Customers';
import InvoiceDrawer from './InvoiceDrawer';
import CustomerDrawer from './CustomerDrawer';
import NewInvoiceModal from './NewInvoiceModal';
import ImportInvoiceModal from './ImportInvoiceModal';

const SUBTABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'receivables', label: 'Receivables' },
  { id: 'customers', label: 'Customers' },
];

// B2B / Accounts Receivable. Team-visible (not admin-gated). Reads via PostgREST
// (computed views + b2b_report RPC); every write goes through authWrite so it
// lands in auth_audit_log. See migration 154.
export default function B2B({ session }) {
  const toast = useToast();
  const [tab, setTab] = useState('receivables');
  const [invoices, setInvoices] = useState([]);     // b2b_invoice_computed
  const [customers, setCustomers] = useState([]);   // b2b_customer_rollup
  const [fx, setFx] = useState({});                 // currency_code -> rate_to_aud
  const [report, setReport] = useState(null);       // b2b_report()
  const [trackingBy, setTrackingBy] = useState({}); // invoice_id -> [tracking rows]
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [openInvoiceId, setOpenInvoiceId] = useState(null);
  const [openCustomerId, setOpenCustomerId] = useState(null);
  const [creating, setCreating] = useState(null);   // {} or {customer_id} to prefill
  const [importing, setImporting] = useState(false); // PO/PDF import modal

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [inv, cust, fxr, rep, trk] = await Promise.all([
        aws.from('b2b_invoice_computed').select('*'),
        aws.from('b2b_customer_rollup').select('*').order('lifetime_spend', { ascending: false }),
        aws.from('fx_rates').select('*'),
        aws.rpc('b2b_report'),
        aws.from('b2b_tracking').select('invoice_id, carrier, tracking_url'),
      ]);
      if (inv.error) throw inv.error;
      setInvoices(inv.data || []);
      setCustomers(cust.data || []);
      const map = {}; (fxr.data || []).forEach(r => { map[r.currency_code] = r.rate_to_aud; });
      setFx(map);
      setReport(rep.data || null);
      const tb = {}; (trk.data || []).forEach(t => { (tb[t.invoice_id] = tb[t.invoice_id] || []).push(t); });
      setTrackingBy(tb);
    } catch (e) {
      setErr(e?.message || 'Could not load B2B data');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openInvoiceId_ = (id) => { setOpenCustomerId(null); setOpenInvoiceId(id); };

  return (
    <div className="app-screen" style={{ background: '#f7f8fa', minHeight: '100%' }}>
      <div className="main-content" style={{ maxWidth: 1320, margin: '0 auto', padding: '24px 28px' }}>
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0F1419', margin: 0 }}>B2B</h1>
            <div style={{ fontSize: 12, color: '#8B9094', marginTop: 2 }}>Wholesale orders &amp; accounts receivable</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setImporting(true)}>↑ Import PO/PDF</button>
            <button className="btn btn-primary btn-sm" onClick={() => setCreating({})}>+ New invoice</button>
          </div>
        </div>

        {/* sub-tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 18, borderBottom: '1px solid #e4e4e7' }}>
          {SUBTABS.map(s => (
            <button key={s.id} onClick={() => setTab(s.id)}
              style={{
                border: 'none', background: 'none', cursor: 'pointer', padding: '8px 14px',
                fontSize: 13, fontWeight: 600,
                color: tab === s.id ? '#0F1419' : '#8B9094',
                borderBottom: tab === s.id ? '2px solid #0F1419' : '2px solid transparent',
                marginBottom: -1,
              }}>
              {s.label}
            </button>
          ))}
        </div>

        {err && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
            {err} <button onClick={load} style={{ marginLeft: 8, textDecoration: 'underline', background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer' }}>Retry</button>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#a1a1aa', fontSize: 13 }}>Loading…</div>
        ) : (
          <>
            {tab === 'dashboard' && <Dashboard invoices={invoices} customers={customers} fx={fx} report={report} onOpenInvoice={openInvoiceId_} onGoReceivables={() => setTab('receivables')} />}
            {tab === 'receivables' && <Receivables session={session} invoices={invoices} customers={customers} fx={fx} trackingBy={trackingBy} onOpenInvoice={openInvoiceId_} />}
            {tab === 'customers' && <Customers session={session} customers={customers} fx={fx} onOpenCustomer={setOpenCustomerId} />}
          </>
        )}
      </div>

      {openInvoiceId && (
        <InvoiceDrawer
          invoiceId={openInvoiceId}
          session={session}
          fx={fx}
          onClose={() => setOpenInvoiceId(null)}
          onChanged={load}
        />
      )}
      {openCustomerId && (
        <CustomerDrawer
          customerId={openCustomerId}
          customers={customers}
          invoices={invoices}
          session={session}
          fx={fx}
          onClose={() => setOpenCustomerId(null)}
          onOpenInvoice={openInvoiceId_}
          onNewInvoice={(cid) => setCreating({ customer_id: cid })}
          onChanged={load}
        />
      )}
      {creating && (
        <NewInvoiceModal
          prefill={creating}
          customers={customers}
          session={session}
          onClose={() => setCreating(null)}
          onCreated={(id) => { setCreating(null); load().then(() => setOpenInvoiceId(id)); }}
        />
      )}
      {importing && (
        <ImportInvoiceModal
          customers={customers}
          session={session}
          onClose={() => setImporting(false)}
          onCreated={(id) => { setImporting(false); load().then(() => setOpenInvoiceId(id)); }}
        />
      )}
    </div>
  );
}
